import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { Icon } from '../components/icons';
import { PageHeader, SectionCard, Spinner } from '../components/ui';
import { fmtDate, fmtMoney } from '../lib/format';

interface Summary {
  total: { rake: number; entries: number };
  byRoom: { roomId: string; name: string; rake: number; entries: number }[];
  byDay: { date: string; rake: number }[];
}

interface LogItem {
  id: string;
  roomId: string;
  roomName: string | null;
  ownerUserId: string;
  ownerNickname: string | null;
  amount: number;
  bankBefore: number;
  rakePct: number;
  ts: number;
  kind?: 'rake' | 'withdrawal';
  withdrawnBy?: string | null;
}

export function EarningsPage() {
  const [summary, setSummary] = useState<Summary | null>(null);
  const [log, setLog] = useState<LogItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get<Summary>('/api/crm/earnings/summary'),
      api.get<{ items: LogItem[] }>('/api/crm/earnings/log?limit=200')
    ])
      .then(([s, l]) => { setSummary(s); setLog(l.items); })
      .finally(() => setLoading(false));
  }, []);

  if (loading || !summary) {
    return <div className="flex items-center gap-2 text-stone-400 text-sm"><Spinner /> Загрузка…</div>;
  }

  const maxDay = Math.max(1, ...summary.byDay.map((d) => d.rake));

  return (
    <div>
      <PageHeader title="Заработок" subtitle={`Записей: ${summary.total.entries}`} />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <div className="stat-card">
          <div className="label">Всего рейка</div>
          <div className="value text-emerald-300">{fmtMoney(summary.total.rake)}</div>
        </div>
        <div className="stat-card">
          <div className="label">Партий с рейком</div>
          <div className="value">{summary.total.entries}</div>
        </div>
        <div className="stat-card">
          <div className="label">Средний рейк</div>
          <div className="value">{fmtMoney(summary.total.entries ? Math.round(summary.total.rake / summary.total.entries) : 0)}</div>
        </div>
        <div className="stat-card">
          <div className="label">Активных комнат</div>
          <div className="value">{summary.byRoom.length}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <SectionCard title="Динамика по дням" className="lg:col-span-1">
          {summary.byDay.length === 0 ? (
            <div className="text-sm text-stone-500 py-4">Пока нет данных</div>
          ) : (
            <div className="space-y-1.5">
              {summary.byDay.map((d) => (
                <div key={d.date} className="flex items-center gap-2">
                  <div className="w-20 text-[11px] text-stone-500 font-mono">{d.date}</div>
                  <div className="flex-1 h-2 rounded-full bg-white/5 overflow-hidden">
                    <div className="h-full bg-amber-400/80" style={{ width: `${(d.rake / maxDay) * 100}%` }} />
                  </div>
                  <div className="w-20 text-right text-xs font-mono text-stone-300">{fmtMoney(d.rake)}</div>
                </div>
              ))}
            </div>
          )}
        </SectionCard>

        <SectionCard title="Топ комнат по рейку" className="lg:col-span-2">
          {summary.byRoom.length === 0 ? (
            <div className="text-sm text-stone-500 py-4">Пока нет данных</div>
          ) : (
            <div className="overflow-x-auto -mx-3">
              <table className="table-clean">
                <thead>
                  <tr>
                    <th>Комната</th>
                    <th>Партий</th>
                    <th>Рейк</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.byRoom.map((r) => (
                    <tr key={r.roomId}>
                      <td>{r.name}</td>
                      <td className="font-mono">{r.entries}</td>
                      <td className="font-mono text-emerald-300">{fmtMoney(r.rake)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </SectionCard>
      </div>

      <SectionCard title="Лог рейка и снятий кассы" className="mt-4">
        {log.length === 0 ? (
          <div className="text-sm text-stone-500 py-4">Пока нет записей</div>
        ) : (
          <div className="overflow-x-auto -mx-3">
            <table className="table-clean">
              <thead>
                <tr>
                  <th>Тип</th>
                  <th>Время</th>
                  <th>Комната</th>
                  <th>Владелец / снял</th>
                  <th>Банк до</th>
                  <th>%</th>
                  <th>Сумма</th>
                </tr>
              </thead>
              <tbody>
                {log.map((l) => {
                  const isWithdrawal = l.kind === 'withdrawal';
                  return (
                    <tr key={l.id} className={isWithdrawal ? 'bg-amber-400/5' : ''}>
                      <td>
                        {isWithdrawal ? (
                          <span className="pill pill-amber">Снятие</span>
                        ) : (
                          <span className="pill pill-stone">Рейк</span>
                        )}
                      </td>
                      <td className="text-xs text-stone-400 whitespace-nowrap">{fmtDate(l.ts)}</td>
                      <td>{l.roomName ?? l.roomId.slice(0, 8)}</td>
                      <td>
                        {isWithdrawal
                          ? <span className="text-amber-300/80">CRM: {l.withdrawnBy?.slice(0, 8) ?? '—'}</span>
                          : (l.ownerNickname ?? <span className="text-stone-500">{l.ownerUserId?.slice(0, 8)}</span>)}
                      </td>
                      <td className="font-mono">{fmtMoney(l.bankBefore)}</td>
                      <td className="font-mono">{l.rakePct.toFixed(2)}%</td>
                      <td className={`font-mono ${isWithdrawal ? 'text-amber-300' : 'text-emerald-300'}`}>
                        {isWithdrawal ? '−' : '+'}{fmtMoney(l.amount)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>
    </div>
  );
}
